import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { deleteVendor, searchVendor } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import { setPagination } from '../../redux/Actions/TableAction';
import AppTable from '../../components/AppTable/AppTable';
import DeleteButton from '../../components/Delete/DeleteButton';
import EditButton from '../../components/Edit/EditButton';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';
import { hasPermission } from '../../utils/permissionUtils';
import { getSelectedUnit } from '../../storage/Storage';

function VendorManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Permissions
    const canEditVendor = hasPermission('vendor', 'write');
    const canReadVendor = hasPermission('vendor', 'read');
    const canDeleteVendor = hasPermission('vendor', 'delete');
    const canAddVendor = hasPermission('vendor', 'write');

    useEffect(() => {
        if (canReadVendor) {
            getVendors();
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadVendor]);

    const getVendors = (searchKey, searchValue) => {
        if (!canReadVendor) {
            toast.error('You do not have permission to view vendors');
            return;
        }

        setIsSearching(true);

        const searchObj = {
            unitIds: getSelectedUnit()?._id
        };

        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchVendor)
            .then((res) => {
                const vendorData = Array.isArray(res?.data?.docs) ? res?.data.docs : [];
                dispatch(setApiJson(vendorData));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
            })
            .catch(() => {
                dispatch(setApiJson([]));
                toast.error('Error fetching vendors. Please try again.');
            })
            .finally(() => {
                setIsSearching(false);
            });
    };

    const handleSearch = () => {
        if (!canReadVendor) {
            toast.error('You do not have permission to search vendors');
            return;
        }

        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }

        getVendors(optionSelect, valueSearched.trim());
    };

    const handleClearSearch = () => {
        if (!canReadVendor) {
            toast.error('You do not have permission to access vendors');
            return;
        }

        setOptionSelect('');
        setValueSearched('');
        getVendors();
    };

    const handleEdit = (row) => {
        if (!canEditVendor) {
            toast.error('You do not have permission to edit vendors');
            return;
        }
        navigate(`/vendormanagement/edit/${row._id}`);
    };

    const handleDelete = (row) => {
        if (!canDeleteVendor) {
            toast.error('You do not have permission to delete vendors');
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${row.vendorName}?`)) {
            const json = { _id: row?._id };
            HitApi(json, deleteVendor)
                .then((res) => {
                    if (res?.statusCode === 200) {
                        toast.success('Vendor deleted successfully!');
                        getVendors();
                    } else {
                        toast.error('Failed to delete vendor. Please try again.');
                    }
                })
                .catch(() => {
                    toast.error('Error occurred while deleting vendor. Please try again.');
                });
        }
    };



    const baseIndex = ((TableDataReducer.pagination?.page || 1) - 1) * (TableDataReducer.pagination?.limit || 10);

    const baseTableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, row, index) => (
                <div className="text-center">
                    <span className="text-sm font-medium text-gray-900">{baseIndex + index + 1}</span>
                </div>
            )
        },
        {
            title: "Vendor Name",
            key: "vendorName",
            render: (value) => (
                <div className="flex items-center">
                    <span className="font-medium">{value || 'N/A'}</span>
                </div>
            )
        },
        {
            title: "GST Number",
            key: "gst",
            render: (value) => (
                <span className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                    {value || 'N/A'}
                </span>
            )
        },
        {
            title: "Email",
            key: "email",
            render: (value) => (
                <span className="text-sm text-gray-600">{value || 'N/A'}</span>
            )
        },
        {
            title: "Contact",
            key: "contact",
            render: (value) => value || 'N/A'
        },
        {
            title: "Address",
            key: "address",
            render: (value) => (
                <span className="text-sm text-gray-600 capitalize">{value || 'N/A'}</span>
            )
        },
        {
            title: "State",
            key: "state",
            render: (value) => <span className="capitalize">{value || 'N/A'}</span>
        },
    
    ];

    // Add Actions column if permissions allow
    if (canEditVendor || canDeleteVendor) {
        baseTableHeaders.push({
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => (
                <div className="flex justify-center space-x-2">
                    {canEditVendor && (
                        <EditButton
                            onEdit={() => handleEdit(row)}
                            itemName="vendor"
                        />
                    )}
                    {canDeleteVendor && (
                        <DeleteButton
                            onDelete={() => handleDelete(row)}
                            itemName="vendor"
                        />
                    )}
                </div>
            )
        });
    }

    const searchOptions = [
        { label: 'Vendor Name', value: 'vendorName' },
        { label: 'GST Number', value: 'gst' },
        { label: 'Email', value: 'email' },
        { label: 'Contact', value: 'contact' },
        { label: 'Address', value: 'address' },
        { label: 'State', value: 'state' },
        { label: 'Status', value: 'status' },
    ];

    if (!canReadVendor) {
        return (
            <div className="p-5">
                <PageHeader title="Vendor Management" description="View and manage vendors" />
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view vendors.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader title="Vendor Management" description="View and manage vendors" />
            <AppTable
                TH={baseTableHeaders}
                TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
                isLoading={!Array.isArray(ApiReducer.apiJson) || isSearching}
                emptyMessage="No vendors found"
                buttonText={canAddVendor ? "Add Vendor" : undefined}
                navigateTo={canAddVendor ? "/vendormanagement/add" : undefined}
                TableSearch={
                    <TableSearch
                        options={searchOptions}
                        onClick={handleSearch}
                        optionSelect={optionSelect}
                        setOptionSelect={setOptionSelect}
                        setValueSearched={setValueSearched}
                        valueSearched={valueSearched}
                        onClear={handleClearSearch}
                    />
                }
            />
        </div>
    );
}

export default VendorManagement;
